import { useCallback } from 'react';
import {
  useAuth as useFronteggAuth,
  useAuthActions as useFronteggAuthActions,
} from '@frontegg/react';
import { getLogger } from '../../libs/logger';
import { runWithRequestAuthorizeCorrelation } from '../../services/auth/authSessionTelemetry';
import { clearStorage } from '../../services/provider/authService';
import { useGrowthBookFlag } from '../useGrowthBookFlag/useGrowthBookFlag';
import { AUTH_SESSION_TELEMETRY_FLAG } from '../../utils/authSessionTelemetryFlag';
import { reset as resetAnalytics } from '../../features/tracking/track';

const logger = getLogger('useAuth');

export enum LinkedProvider {
  LOCAL = 'local',
  GOOGLE = 'google',
}

export type Role = {
  createdAt: string;
  description: string;
  firstUserRole: boolean;
  id: string;
  isDefault: boolean;
  key: string;
  level: number;
  name: string;
  permissions: string[];
  tenantId: string;
  updatedAt: string;
  vendorId: string;
};

export type Permission = {
  id: string;
  key: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  categoryId: string;
  fePermission: boolean;
};

type Tenant = {
  tenantId: string;
  isDisabled: boolean;
  expirationDate: string;
  roles: Array<Role>;
};

type AuthState = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: {
    id: string;
    accessToken: string;
    email: string;
    metadata?: string;
    name?: string;
    tenants?: Array<Tenant>;
    tenantId?: string;
    tenantIds?: string[];
    roles?: Array<Role>;
    permissions?: Array<Permission>;
  };
};

export type UserMetadata = {
  legacyUserId?: string;
  linked?: boolean;
  firstName?: string;
  lastName?: string;
  isUserPoolUser?: boolean;
  linkedProviders?: LinkedProvider[];
  googleLinked?: string[];
  isInvited?: boolean;
};

export const parseUserMetadata = (metadata?: string): UserMetadata => {
  try {
    return metadata ? JSON.parse(metadata) : {};
  } catch {
    return {};
  }
};

/**
 * Interprets the Frontegg JWT `user.metadata` JSON string.
 *
 * @returns `null` when metadata is not a non-empty string yet (caller cannot
 * decide from the token); otherwise whether `legacyUserId` is non-empty
 * after parse and trim.
 */
export const parseLegacyUserIdPresence = (
  metadata?: string | null,
): boolean | null => {
  if (metadata == null || typeof metadata !== 'string' || !metadata.trim()) {
    return null;
  }
  const legacy = parseUserMetadata(metadata).legacyUserId;
  return Boolean(String(legacy ?? '').trim());
};

export const useUserNames = (): {
  firstName?: string;
  lastName?: string;
} => {
  const { user } = useAuth();
  const metadata = parseUserMetadata(user?.metadata);
  return {
    firstName: metadata?.firstName,
    lastName: metadata?.lastName,
  };
};

export const useTenantId = (): string | undefined => {
  const { user } = useAuth();
  return user?.tenantId;
};

export const useUserRoles = (): {
  roles: Array<Role>;
} => {
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  if (!tenantId) return { roles: [] };

  const roles =
    user?.tenants?.find((tenant) => tenant.tenantId === tenantId)?.roles ?? [];

  return { roles };
};

export const useUserPermissions = (): {
  permissions: Array<Permission>;
} => {
  const { user } = useAuth();
  return { permissions: user?.permissions ?? [] };
};

export const useLinkedProviders = (): {
  isGoogleLinked: boolean;
  hasLocalProvider: boolean;
} => {
  const { user } = useAuth();
  const metadata = parseUserMetadata(user?.metadata);
  return {
    isGoogleLinked: Boolean(
      metadata.linkedProviders?.includes(LinkedProvider.GOOGLE) &&
      metadata.googleLinked?.length,
    ),
    hasLocalProvider:
      !metadata.linkedProviders?.length ||
      metadata.linkedProviders.includes(LinkedProvider.LOCAL),
  };
};

export const useAuth = (): AuthState => useFronteggAuth() as AuthState;

type AuthActions = {
  logout: () => Promise<void>;
  silentLogout: () => Promise<void>;
  requestAuthorize: () => Promise<void>;
  loadEntitlements: () => Promise<void>;
};

export const useAuthActions = (): AuthActions => {
  const isTelemetryEnabled =
    useGrowthBookFlag(AUTH_SESSION_TELEMETRY_FLAG) === true;
  const { requestAuthorize: fronteggRequestAuthorize, ...rest } =
    useFronteggAuthActions();

  const requestAuthorize = useCallback(async (): Promise<void> => {
    if (isTelemetryEnabled) {
      await runWithRequestAuthorizeCorrelation(() =>
        fronteggRequestAuthorize(),
      );
      return;
    }

    await fronteggRequestAuthorize();
  }, [fronteggRequestAuthorize, isTelemetryEnabled]);

  return {
    ...rest,
    requestAuthorize,
  };
};

/**
 * Logout user from frontegg and remove local storage creds
 */
export const useClearAuthentication = (): (() => Promise<void>) => {
  const { silentLogout } = useAuthActions();
  return useCallback(async (): Promise<void> => {
    logger.info('[useClearAuthentication] clearing authentication');
    await silentLogout();
    clearStorage();
    resetAnalytics();
  }, [silentLogout]);
};
